const MAX_LONG_EDGE = 1568;
const JPEG_QUALITY = 0.85;

function fitWithin(w: number, h: number, maxLong: number): { width: number; height: number } {
    const long = Math.max(w, h);
    if (long <= maxLong) return { width: w, height: h };
    const scale = maxLong / long;
    return {
        width: Math.round(w * scale),
        height: Math.round(h * scale),
    };
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = src;
    });
}

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") resolve(reader.result);
            else reject(new Error("Failed to read file"));
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
    });
}

function isImageFile(file: File): boolean {
    if (file.type.startsWith("image/")) return true;
    return /\.(heic|heif|jpe?g|png|webp|gif|bmp|tif)$/i.test(file.name);
}

async function decodeImageSource(
    file: File,
): Promise<{ width: number; height: number; source: CanvasImageSource; cleanup?: () => void }> {
    if (typeof createImageBitmap !== "undefined") {
        const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
        return {
            width: bitmap.width,
            height: bitmap.height,
            source: bitmap,
            cleanup: () => bitmap.close(),
        };
    }
    const objectUrl = URL.createObjectURL(file);
    try {
        const img = await loadImage(objectUrl);
        return { width: img.naturalWidth, height: img.naturalHeight, source: img };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

function drawToJpegDataUrl(source: CanvasImageSource, width: number, height: number, maxLongEdge: number): string {
    const { width: w, height: h } = fitWithin(width, height, maxLongEdge);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    ctx.drawImage(source, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

/**
 * Downscale a camera data URL so the long edge is at most `maxLongEdge` px.
 * Returns JPEG data URL + dimensions for vision cost estimation.
 */
export async function downscaleDataUrl(
    dataUrl: string,
    maxLongEdge: number = MAX_LONG_EDGE,
): Promise<{ dataUrl: string; width: number; height: number }> {
    const img = await loadImage(dataUrl);
    const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, maxLongEdge);
    if (width === img.naturalWidth && height === img.naturalHeight && dataUrl.startsWith("data:image/jpeg")) {
        return { dataUrl, width, height };
    }
    const jpeg = drawToJpegDataUrl(img, img.naturalWidth, img.naturalHeight, maxLongEdge);
    return { dataUrl: jpeg, width, height };
}

/**
 * Decode an image File (including HEIC on iOS WebKit), downscale, and return
 * a JPEG data URL. Falls back to the raw file data URL if decode fails so the
 * server-side HEIC net can still convert.
 */
export async function fileToNormalizedDataUrl(file: File, maxLongEdge: number = MAX_LONG_EDGE): Promise<string> {
    if (!isImageFile(file)) {
        return readFileAsDataUrl(file);
    }
    try {
        const { width, height, source, cleanup } = await decodeImageSource(file);
        try {
            return drawToJpegDataUrl(source, width, height, maxLongEdge);
        } finally {
            cleanup?.();
        }
    } catch {
        return readFileAsDataUrl(file);
    }
}

/**
 * Normalize an image File to JPEG (HEIC -> JPEG on iOS, downscale large photos).
 * Returns the original file unchanged when decode fails or the file is not an image.
 */
export async function normalizeImageFile(file: File, maxLongEdge: number = MAX_LONG_EDGE): Promise<File> {
    if (!isImageFile(file)) return file;
    try {
        const dataUrl = await fileToNormalizedDataUrl(file, maxLongEdge);
        if (/^data:image\/(heic|heif)/i.test(dataUrl)) {
            return file;
        }
        const blob = await fetch(dataUrl).then((r) => r.blob());
        const stem = file.name.replace(/\.[^.]+$/, "") || "photo";
        return new File([blob], `${stem}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
    } catch {
        return file;
    }
}
